import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import type { InboxConversationSummary } from "./use-inbox-conversations";
import type { InboxAiDraft } from "./use-inbox-drafts";

const BASE = "/api";

export interface InboxMessage {
  id: string;
  conversationId: string;
  zernioMsgId: string;
  direction: "in" | "out";
  senderId: string;
  senderName: string | null;
  text: string | null;
  attachmentUrl: string | null;
  attachmentType: string | null;
  /** Structured card (IG/Messenger generic or button template), if any. */
  template?: {
    elements: Array<{
      title: string | null;
      subtitle: string | null;
      imageUrl: string | null;
      buttons: Array<{ type: string; title: string; url: string | null }>;
    }>;
  } | null;
  sentByUserId: string | null;
  sentByAi?: boolean;
  sentAt: string;
  platformSentAt: string | null;
  deliveryStatus: "PENDING" | "SENT" | "FAILED";
}

export type InboxConversationDetail = InboxConversationSummary & {
  aiDraft?: InboxAiDraft | null;
};

interface FirstPage {
  conversation: InboxConversationDetail;
  messages: InboxMessage[];
  nextCursor: string | null;
}

interface MessagesPage {
  messages: InboxMessage[];
  nextCursor: string | null;
}

async function fetchConversation(brandId: string, convId: string): Promise<FirstPage> {
  const r = await fetch(`${BASE}/brands/${brandId}/inbox/conversations/${convId}`, {
    credentials: "include",
  });
  if (!r.ok) throw new Error("Failed to load conversation");
  const json = (await r.json()) as { data: FirstPage };
  return json.data;
}

async function fetchMessagesPage(brandId: string, convId: string, cursor?: string): Promise<MessagesPage> {
  const search = new URLSearchParams();
  if (cursor) search.set("cursor", cursor);
  const r = await fetch(
    `${BASE}/brands/${brandId}/inbox/conversations/${convId}/messages?${search.toString()}`,
    { credentials: "include" },
  );
  if (!r.ok) throw new Error("Failed to load messages");
  const json = (await r.json()) as { data: MessagesPage };
  return json.data;
}

/** First page of a conversation — header + 50 newest messages. Marks the conv as read. */
export function useInboxConversation(brandId: string, convId: string | null | undefined) {
  return useQuery({
    queryKey: ["inbox-conversation", brandId, convId],
    queryFn: () => fetchConversation(brandId, convId!),
    enabled: !!brandId && !!convId,
    staleTime: 10_000,
  });
}

/** Older message pages (cursor pagination). Does NOT mark as read. */
export function useInboxOlderMessages(brandId: string, convId: string | null | undefined, firstCursor: string | null | undefined) {
  return useInfiniteQuery({
    queryKey: ["inbox-conv-messages", brandId, convId],
    queryFn: ({ pageParam }) => fetchMessagesPage(brandId, convId!, pageParam as string | undefined),
    initialPageParam: firstCursor ?? undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!brandId && !!convId && !!firstCursor,
    staleTime: 30_000,
  });
}
